import React from 'react';
import { useHawkSearch } from 'components/StoreProvider';
import { ClientSelectionValue } from 'store/ClientSelections';
import XSVG from 'components/svg/XSVG';
import { Facet, Range } from 'models/Facets';
import { useTranslation } from 'react-i18next';

interface SelectionsProps {
	onFacetChange: () => void;
}

function Selections({ onFacetChange }: SelectionsProps) {
	const {
		store: { facetSelections },
		actor,
	} = useHawkSearch();

	const { t } = useTranslation();

	// Added filter to hide selection for Tabs
	const keys = Object.keys(facetSelections).filter((key) => key !== 'it');

	if (keys.length === 0) {
		// no selections, so render nothing
		return null;
	}

	function clearSelection(facet: string, value?: ClientSelectionValue) {
		if (value) {
			actor.clearFacetValue(facet, value.value);
		} else {
			actor.clearFacet(facet);
		}

		onFacetChange();
	}

	function clearAll() {
		actor.clearAllFacets();
		onFacetChange();
	}

	function renderRange(value: ClientSelectionValue, facet: Facet) {
		const displayValue = value.label || value.value;

		if (!displayValue || displayValue.indexOf(',') === -1) {
			// range facet selection values should include a comma, so if they don't then this likely isn't a valid
			// range value that we want to render
			const selectedRange = facet.Ranges.find((range: Range) => range.Value === value.value);
			return selectedRange?.Label ?? displayValue;
		}

		const displayValueRange = displayValue.split(',');
		if (facet.IsCurrency && displayValueRange.length > 1) {
			if (facet.CurrencySymbol) {
				return `${facet.CurrencySymbol} ${displayValueRange[0]} - ${facet.CurrencySymbol} ${displayValueRange[1]}`;
			}
			return `${displayValueRange[0]} - ${displayValueRange[1]}`;
		}

		// return a prettier display value for ranges
		return displayValue.replace(',', ' - ');
	}

	return (
		<div className="hawk-facet-rail__facet hawk-facet-rail__facet--selections">
			<div className="hawk-facet-rail__selections">
				<h4 className="hawk-facet-rail__selections-heading">{t("You've Selected")}</h4>
				<ul className="hawk-selections">
					{keys.map((key) => {
						const selection = facetSelections[key];

						return (
							<li key={key} className="hawk-selections__category">
								<div className="hawk-selections__category-wrapper">
									<span className="hawk-selections__category-name">
										<button
											onClick={() => clearSelection(key)}
											className="hawk-selections__category-remove"
										>
											<XSVG />
											<span className="hawk-selections__category-name-text">
												{selection.label}
											</span>
											<span className="roc-visually-hidden">
												Unselect all facets for {selection.label}
											</span>
										</button>
									</span>

									<ul className="hawk-selections__item-list">
										{selection.items.map((item) => {
											const negation = item.value.startsWith('-');

											return (
												<li key={item.value} className="hawk-selections__item">
													<button
														onClick={() => clearSelection(key, item)}
														className="hawk-selections__item-remove"
													>
														<div className="hawk-selections__item-icon">
															<XSVG />
														</div>
														<span className="roc-visually-hidden">
															Unselect facet {selection.label} {item.label}
														</span>
														<span
															className={
																negation
																	? 'hawk-selections__item-name hawk-selections__item-name--negated'
																	: 'hawk-selections__item-name'
															}
														>
															{selection.facet.FieldType === 'range'
																? // render ranges in a specific way
																  renderRange(item, selection.facet)
																: // other facets can have their labels rendered directly
																  item.label}
														</span>
													</button>
												</li>
											);
										})}
									</ul>
								</div>
							</li>
						);
					})}
				</ul>

				<div className="hawk-selections__actions">
					<button onClick={clearAll} className="btn-link hawk-selections__actions-item">
						{t('Clear All')}
					</button>
				</div>
			</div>
		</div>
	);
}

export default Selections;
